import type { UseQueryOptions } from '@tanstack/react-query'

import { api, handleAxiosError } from '@/lib/axios'

import type {
  GetAllPPTypeResponseDataType,
  InfiniteQueryOptions,
  PPTypeResponseDataType
} from './types'

const PP_TYPE_QUERY_KEY = (params: {
  page: number
  per_page: number
  search: string
}) => ['reference_pp_type', { ...params }] as const

type PPTypeQueryKey = ReturnType<typeof PP_TYPE_QUERY_KEY>

const getBatch = (
  opts?: Partial<{
    page: number
    per_page: number
    search: string
  }>
): InfiniteQueryOptions<PPTypeQueryKey> => {
  const params = {
    page: opts?.page ?? 1,
    per_page: opts?.per_page ?? 10,
    search: opts?.search ?? ''
  }

  return {
    queryKey: PP_TYPE_QUERY_KEY(params),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      const meta = lastPage.meta

      if (!meta) return undefined
      return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
        ? (meta?.current_page ?? 0) + 1
        : undefined
    },
    queryFn: async ({ pageParam }) => {
      try {
        const res = await api.get<GetAllPPTypeResponseDataType>(
          '/referensi/jenis-pp',
          {
            params: {
              page: pageParam,
              per_page: params.per_page,
              nama:
                params.search.length > 3
                  ? `%${params.search.toUpperCase()}%`
                  : undefined
            }
          }
        )

        return {
          message: res.data.message,
          data: res.data.data,
          meta: res.data.meta
        }
      } catch (ex) {
        throw handleAxiosError(ex)
      }
    }
  }
}

const PP_TYPE_BY_ID_QUERY_KEY = (params: { id: string }) =>
  ['reference_pp_type', params] as const

const byId = (opts: {
  id: string
}): UseQueryOptions<PPTypeResponseDataType> => ({
  queryKey: PP_TYPE_BY_ID_QUERY_KEY(opts),
  queryFn: async () => {
    try {
      const res = await api.get<PPTypeResponseDataType>(
        `/referensi/jenis-pp/${opts.id}`
      )

      const data = res.data.data
      if (!data) {
        throw new Error(`ID jenis PP ${opts.id} tidak dapat ditemukan.`)
      }

      return {
        message: res.data.message,
        data
      }
    } catch (ex) {
      throw handleAxiosError(ex)
    }
  }
})

export const ppTypeReference = {
  queryKey: {
    all: PP_TYPE_QUERY_KEY,
    byId: PP_TYPE_BY_ID_QUERY_KEY
  },
  batched: getBatch,
  byId
}
